import { useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";
import { FaCheck, FaChevronDown } from "react-icons/fa";

const SelectWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: ${(props) => (props.$small ? "auto" : "100%")};
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: ${(props) => (props.$small ? "10px 15px" : "15px 20px")};
  border: 2px solid var(--accent-light);
  border-radius: ${(props) => (props.$small ? "20px" : "15px")};
  font-family: inherit;
  font-size: ${(props) => (props.$small ? "12px" : "16px")};
  ${(props) => (props.$small ? "max-height: 38.5px;" : "")}
  background: var(--surface);
  color: var(--text-primary);
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(124, 14, 51, 0.1);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(props) =>
    props.$isPlaceholder ? "var(--text-muted)" : "var(--text-primary)"};
`;

const Chevron = styled(FaChevronDown)`
  flex-shrink: 0;
  font-size: 0.75em;
  color: var(--accent);
  transition: transform 0.3s ease;
  transform: rotate(${(props) => (props.$open ? "180deg" : "0deg")});
`;

const OptionsList = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  ${(props) => (props.$small ? "right: 0; left: auto;" : "left: 0;")}
  min-width: 100%;
  width: max-content;
  max-width: min(320px, 85vw);
  margin: 0;
  padding: 6px;
  list-style: none;
  background: var(--surface);
  border: 1px solid var(--accent-light);
  border-radius: 12px;
  box-shadow: var(--shadow-soft);
  max-height: 240px;
  overflow-y: auto;
  z-index: 1100;
`;

const OptionItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: ${(props) => (props.$small ? "8px 10px" : "10px 12px")};
  border-radius: 8px;
  font-size: ${(props) => (props.$small ? "12px" : "14px")};
  color: ${(props) =>
    props.$selected ? "var(--accent)" : "var(--text-primary)"};
  font-weight: ${(props) => (props.$selected ? "600" : "400")};
  background: ${(props) =>
    props.$highlighted ? "var(--accent-light)" : "transparent"};
  cursor: pointer;
  transition: background 0.15s ease;
  white-space: nowrap;
`;

const CheckIcon = styled(FaCheck)`
  flex-shrink: 0;
  font-size: 0.7em;
  color: var(--accent);
`;

const CustomSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Izaberite...",
  disabled = false,
  ariaLabel,
  $small = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const listboxId = useId();

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

  const openList = () => {
    if (disabled) return;
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOpen(true);
  };

  const closeList = () => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const selectOption = (option) => {
    if (option.value !== value) {
      onChange(option.value);
    }
    closeList();
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeList();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  // Keep the highlighted option in view
  useEffect(() => {
    if (!isOpen || highlightedIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[highlightedIndex];
    if (item) {
      item.scrollIntoView({ block: "nearest" });
    }
  }, [isOpen, highlightedIndex]);

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (!isOpen) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        closeList();
        break;
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Home":
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case "End":
        e.preventDefault();
        setHighlightedIndex(options.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          selectOption(options[highlightedIndex]);
        }
        break;
      case "Tab":
        closeList();
        break;
      default:
        break;
    }
  };

  return (
    <SelectWrapper ref={wrapperRef} $small={$small}>
      <Trigger
        type="button"
        $small={$small}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={
          isOpen && highlightedIndex >= 0
            ? `${listboxId}-option-${highlightedIndex}`
            : undefined
        }
        onClick={() => (isOpen ? closeList() : openList())}
        onKeyDown={handleKeyDown}
      >
        <TriggerLabel $isPlaceholder={!selectedOption}>
          {selectedOption ? selectedOption.label : placeholder}
        </TriggerLabel>
        <Chevron $open={isOpen} aria-hidden="true" />
      </Trigger>

      {isOpen && (
        <OptionsList ref={listRef} id={listboxId} role="listbox" $small={$small}>
          {options.map((option, index) => (
            <OptionItem
              key={option.value}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={option.value === value}
              $small={$small}
              $selected={option.value === value}
              $highlighted={index === highlightedIndex}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => selectOption(option)}
            >
              <span>{option.label}</span>
              {option.value === value && <CheckIcon aria-hidden="true" />}
            </OptionItem>
          ))}
        </OptionsList>
      )}
    </SelectWrapper>
  );
};

export default CustomSelect;
